import { useEffect, useState } from 'react';
import { FiPlus, FiTruck } from 'react-icons/fi';
import { apiAdminListShippingZones } from '../api/shipping';
import { apiAdminCountries } from '../api/countries';
import ZoneRow from './shipping/ZoneRow';
import NewZoneForm from './shipping/NewZoneDrawer';
import TestPanel from './shipping/TestPanel';

const ZoneTable = ({ zones, onReload, emptyMessage, isInternational = false }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead className="bg-gray-50 text-left">
        <tr>
          <th className="px-3 py-2 font-medium">Zone</th>
          <th className="px-3 py-2 font-medium">Countries</th>
          <th className="px-3 py-2 font-medium">Rate per item</th>
          {isInternational && <th className="px-3 py-2 font-medium">Additional item discount</th>}
          {!isInternational && <th className="px-3 py-2 font-medium">Free above</th>}
          {!isInternational && <th className="px-3 py-2 font-medium">COD surcharge</th>}
          <th className="px-3 py-2 font-medium">Delivery est.</th>
          <th className="px-3 py-2 font-medium">Status</th>
          <th className="px-3 py-2 font-medium">Actions</th>
        </tr>
      </thead>
      <tbody>
        {zones.length === 0 && (
          <tr><td colSpan={isInternational ? 7 : 8} className="p-6 text-center text-sm text-black">{emptyMessage}</td></tr>
        )}
        {zones.map((zone) => (
          <ZoneRow key={zone.code} zone={zone} onSaved={onReload} onDeleted={onReload} isInternational={isInternational} />
        ))}
      </tbody>
    </table>
  </div>
);

const ShippingManager = () => {
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [countries, setCountries] = useState([]);
  const [showAddZone, setShowAddZone] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [zoneResponse, countryResponse] = await Promise.all([
        apiAdminListShippingZones(),
        apiAdminCountries(),
      ]);
      setZones(Array.isArray(zoneResponse?.zones) ? zoneResponse.zones : []);
      setCountries(Array.isArray(countryResponse?.countries) ? countryResponse.countries : []);
    } catch (err) {
      setError(err.message || 'Failed to load zones');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const zonesFor = (serviceType, scope) => zones.filter((zone) =>
    (zone.serviceType || 'standard') === serviceType && zone.scope === scope
  );
  const zoneGroups = [
    { serviceType: 'standard', title: 'Standard Delivery' },
    { serviceType: 'fast', title: 'Fast Delivery' },
  ];

  return (
    <div className=" space-y-6">
      <header className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-black flex items-center gap-2">
          <FiTruck className="text-orange-600" /> Shipping Manager
        </h1>
        <button type="button" onClick={() => setShowAddZone(true)} className="inline-flex items-center gap-2 bg-orange-600 text-white px-4 py-2 rounded text-sm font-medium hover:bg-orange-700">
          <FiPlus /> Add Zone
        </button>
      </header>

      <NewZoneForm open={showAddZone} countries={countries} onClose={() => setShowAddZone(false)} onCreated={() => { setShowAddZone(false); load(); }} />

      <section>
        {loading && <p className="p-6 text-sm text-black">Loading…</p>}
        {error && <p className="p-6 text-sm text-red-600">{error}</p>}
        {!loading && !error && (
          <div className="space-y-8">
            {zoneGroups.map(({ serviceType, title }) => (
              <div key={serviceType} className="space-y-4">
                <h2 className="text-xl font-bold text-black">{title}</h2>
                <div className="bg-white border-2 border-orange-200 rounded-lg overflow-hidden">
                  <div className="bg-orange-50 border-b border-orange-200 px-4 py-3">
                    <h3 className="font-semibold text-orange-900">India</h3>
                    <p className="text-xs text-orange-700">Domestic {title.toLowerCase()} zones</p>
                  </div>
                  <ZoneTable zones={zonesFor(serviceType, 'domestic')} onReload={load} emptyMessage={`No India ${title.toLowerCase()} zones available.`} />
                </div>
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-blue-50 border-b border-blue-200 px-4 py-3">
                    <h3 className="font-semibold text-blue-900">International</h3>
                    <p className="text-xs text-blue-700">International {title.toLowerCase()} zones</p>
                  </div>
                  <ZoneTable zones={zonesFor(serviceType, 'international')} onReload={load} emptyMessage={`No international ${title.toLowerCase()} zones available.`} isInternational />
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <TestPanel />
    </div>
  );
};

export default ShippingManager;
